import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import { UserPlus, ChevronRight } from 'lucide-react';
import api from '../utils/api';

/**
 * "Tasks assigned to me from outside my team" heads-up feed.
 *
 * Feb 2026 (iter48): Introduced after task-creation RBAC was fully opened up
 * — any admin/member can now drop a task on anyone. This card gives the
 * assignee a visible surface for cross-team work landing on their plate so
 * there are no surprises at deadline time.
 *
 * Backend: GET /api/tasks/cross-team returns non-completed tasks assigned
 * to me where the creator sits outside my team (SA-created tasks are
 * excluded since those are the routine top-down flow). Renders nothing when
 * the list is empty to keep the dashboard clutter-free.
 */
export function CrossTeamTasksCard() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get('/tasks/cross-team')
        .then(res => { if (!cancelled) { setRows(res.data || []); setLoaded(true); } })
        .catch(() => { if (!cancelled) setLoaded(true); });
    };
    load();
    // Refresh on any WS notification (new task assigned, transferred, etc.)
    const handler = () => load();
    window.addEventListener('ws-notification', handler);
    return () => { cancelled = true; window.removeEventListener('ws-notification', handler); };
  }, []);

  if (!loaded || rows.length === 0) return null;

  const now = new Date();
  return (
    <Card className="border-amber-200 bg-amber-50/40 shadow-sm mb-6" data-testid="cross-team-tasks-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-amber-900 flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Tasks assigned to me from outside my team
            <Badge variant="outline" className="ml-1 text-[10px] bg-white border-amber-300 text-amber-800">{rows.length}</Badge>
          </CardTitle>
          <Link to="/tasks?queue=mine" className="text-xs text-amber-800 hover:underline flex items-center gap-0.5" data-testid="cross-team-view-all">
            View all <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
        <p className="text-[11px] text-amber-800/80 mt-0.5">A heads-up feed of work landing on your plate from people outside your team. Super Admin assignments are excluded.</p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-amber-100">
          {rows.slice(0, 6).map(t => {
            const d = t.deadline ? new Date(t.deadline) : null;
            const overdue = d && d < now;
            const soon = d && !overdue && (d - now) < 48 * 3600 * 1000;
            return (
              <Link key={t.id} to={`/tasks?highlight=${t.id}`} className="flex items-center justify-between px-4 py-2.5 hover:bg-amber-100/40 transition-colors" data-testid={`cross-team-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900 truncate">{t.title}</span>
                    <Badge variant="outline" className="text-[10px] bg-white text-slate-600 border-slate-200">{t.service_name}</Badge>
                    <Badge variant="outline" className="text-[10px] bg-white text-slate-500 border-slate-200">{t.status?.replace(/_/g, ' ')}</Badge>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                    <b className="text-slate-700">{t.created_by_name}</b>
                    {t.created_by_team_lead && t.created_by_team_lead !== t.created_by_name && (
                      <span> · {t.created_by_team_lead}&apos;s team</span>
                    )}
                    {t.client_name && <span> · {t.client_name}</span>}
                  </p>
                </div>
                <div className="text-right ml-3 shrink-0">
                  {d && (
                    <span className={`text-[11px] font-medium ${overdue ? 'text-red-600' : soon ? 'text-amber-700' : 'text-slate-500'}`}>
                      {d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
        {rows.length > 6 && (
          <div className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-t border-amber-100 text-center">
            +{rows.length - 6} more — <Link to="/tasks?queue=mine" className="underline">see the full queue</Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
